import { selectUserFormData } from "app/redux/selector";
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import CreateTicketRow from "../../../../components/CreateTicketRow";
import { shouldShowAddTaskIcon } from "../../../../helpers";
import {
  selectGroupsPagination,
  selectShowCreateTicketIntoGroup,
} from "../../redux/selector";
import { ticketGroupsActions } from "../../redux/slice";
import AddTaskButton from "./AddTaskButton";
import EmptyRow from "./EmptyRow";
import GroupHeaders from "./GroupHeaders";
import { Row } from "./Row";
import RowLoading from "./RowLoading";
import { GroupBlockProps } from "./types";

export const GroupBlock: React.FC<GroupBlockProps> = ({
  group,
  gridTemplate,
  cols,
  minTableWidth,
  selectedTickets,
  onDragEnd,
  groupTicketsLoading,
  boardData,
  groupLoading,
  idx,
  setCols,
  emptyText,
  onCommitColumnWidths,
}) => {
  const dispatch = useDispatch();
  const loadRef = useRef(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const { selectedProjectId } = useSelector(selectUserFormData);
  const [isHovered, setIsHovered] = React.useState(false);

  const showAddTaskIcon = useSelector(selectShowCreateTicketIntoGroup);
  const isAddTaskVisible = shouldShowAddTaskIcon(showAddTaskIcon, group._id);
  const pagination = useSelector(selectGroupsPagination)?.[group._id];



  // ✅ Load more on scroll
  const loadMore = useCallback(() => {
    if (!pagination?.hasMore) return;
    dispatch(
      ticketGroupsActions.loadMoreGroupTickets({
        groupId: group._id,
        batchSize: 4,
      }),
    );
  }, [pagination, group._id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    if (loadRef.current) observer.observe(loadRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <section>
      {/* Group header */}
      <GroupHeaders
        setCols={setCols}
        group={group}
        gridTemplate={gridTemplate}
        minTableWidth={minTableWidth}
        cols={cols}
        boardData={boardData}
        selectedTickets={selectedTickets}
        groupLoading={groupLoading}
        onCommitColumnWidths={onCommitColumnWidths}
      />

      {/* Rows + drop zone */}
      {!group.isCollapse && (
        <div ref={dropRef} className="pe-[24px] ps-[62px]">
          {groupTicketsLoading ? (
            // 🔹 Loading
            group?.tickets?.map((_, idx) => (
              <RowLoading
                key={idx}
                gridTemplate={gridTemplate}
                minTableWidth={minTableWidth}
                cols={cols}
              />
            ))
          ) : group?.ticketsList?.length === 0 ? (
            // 🔹 Empty state
            <EmptyRow
              minTableWidth={minTableWidth}
              emptyText={emptyText as string}
            />
          ) : (
            // 🔹 Data
            group.ticketsList.map((t, i) => (
              <React.Fragment key={t?._id}>
                <Row
                  ticket={t}
                  index={i}
                  group={group}
                  gridTemplate={gridTemplate}
                  minTableWidth={minTableWidth}
                  cols={cols}
                  selectedTickets={selectedTickets}
                  projectId={selectedProjectId}
                />

                {pagination?.hasMore &&
                  i === Math.floor(group.ticketsList.length * 0.75) - 1 && (
                    <div ref={loadRef} className="h-[1px]" />
                  )}
              </React.Fragment>
            ))
          )}
        </div>
      )}

      {isAddTaskVisible && (
        <CreateTicketRow group={group} minTableWidth={minTableWidth} />
      )}

      {!isAddTaskVisible && (
        <AddTaskButton
          setIsHovered={(val) => setIsHovered(val)}
          isHovered={isHovered}
          groupId={group?._id}
          minTableWidth={minTableWidth}
        />
      )}
    </section>
  );
};
